/**
 * useAccessibility.ts
 * Hook pour utiliser AccessibilityService dans les composants React
 */
import { useEffect, useState, useCallback } from 'react';
import { AccessibilityService, accessibilityCSS } from '../services/AccessibilityService';

/**
 * Hook pour gérer l'accessibilité
 */
export function useAccessibility() {
  const [accessibilityService] = useState(() => AccessibilityService.getInstance());
  const [isScreenReaderEnabled, setIsScreenReaderEnabled] = useState(false);
  const [isHighContrastEnabled, setIsHighContrastEnabled] = useState(false);
  const [isReducedMotionEnabled, setIsReducedMotionEnabled] = useState(false);

  useEffect(() => {
    // Initialiser le service
    accessibilityService.initialize();

    // Mettre à jour les états
    setIsScreenReaderEnabled(accessibilityService.isScreenReaderEnabled());
    setIsHighContrastEnabled(accessibilityService.isHighContrastEnabled());
    setIsReducedMotionEnabled(accessibilityService.isReducedMotionEnabled());

    // Injecter le CSS d'accessibilité
    const styleId = 'accessibility-css';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = accessibilityCSS;
      document.head.appendChild(style);
    }

    // Nettoyer à la destruction
    return () => {
      // Le service est un singleton, on ne le détruit pas
      // Mais on peut nettoyer les styles si nécessaire
    };
  }, [accessibilityService]);

  /**
   * Annonce un message à l'utilisateur (lecteur d'écran)
   */
  const announce = useCallback(
    (message: string) => {
      accessibilityService.announceToUser(message);
    },
    [accessibilityService]
  );

  /**
   * Définit le focus sur un élément
   */
  const setFocus = useCallback(
    (element: HTMLElement) => {
      accessibilityService.setFocus(element);
    },
    [accessibilityService]
  );

  /**
   * Obtient l'historique du focus
   */
  const getFocusHistory = useCallback(() => {
    return accessibilityService.getFocusHistory();
  }, [accessibilityService]);

  return {
    isScreenReaderEnabled,
    isHighContrastEnabled,
    isReducedMotionEnabled,
    announce,
    setFocus,
    getFocusHistory,
    service: accessibilityService,
  };
}
